import React, { useEffect, useRef } from "react";
import { SidebarItem } from "./SidebarItem";
import {
    IconLayoutSidebarLeftCollapse,
    IconLayoutSidebarRightCollapse,
} from "@tabler/icons-react";
// Tool navigation items
const tools = [
    {
        name: "Case Converter",
        path: "/case-converter",
        icon: "IconLetterCase",
    },
    {
        name: "Git Helper",
        path: "/git-helper",
        icon: "git-branch",
    },
    {
        name: "JIRA Ticket Creator",
        path: "/jira-creator",
        icon: "ticket",
    },
    // {
    //   name: 'Diff Tool',
    //   path: '/diff-tool',
    //   icon: 'diff'
    // },
    {
        name: "List Tool",
        path: "/duplicate-remover",
        icon: "IconCopyOff",
    },
    {
        name: "JSON <> YAML",
        path: "/json-yaml-converter",
        icon: "code",
    },
    {
        name: "UUID Generator",
        path: "/uuid-generator",
        icon: "key",
    },
    {
        name: "Image Resizer",
        path: "/image-resizer",
        icon: "photo",
    },
    {
        name: "React Generator",
        path: "/react-generator",
        icon: "brand-react",
    },
];

interface SidebarProps {
    onCollapseChange?: (collapsed: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onCollapseChange }) => {
    const [isCollapsed, setIsCollapsed] = React.useState(false);
    const [showDialog, setShowDialog] = React.useState(false);
    const userToggled = useRef(false);

    // Set initial collapse state based on screen size
    useEffect(() => {
        const isSmallScreen = window.innerWidth < 768;
        if (!userToggled.current) {
            setIsCollapsed(isSmallScreen);
            if (onCollapseChange) {
                onCollapseChange(isSmallScreen);
            }
        }

        const handleResize = () => {
            const isSmallScreen = window.innerWidth < 768;
            // Only auto-collapse if user hasn't manually toggled
            if (!userToggled.current) {
                setIsCollapsed(isSmallScreen);
                if (onCollapseChange) {
                    onCollapseChange(isSmallScreen);
                }
            }
        };

        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
    }, [onCollapseChange]);

    // Notify parent of initial state
    useEffect(() => {
        if (onCollapseChange) {
            onCollapseChange(isCollapsed);
        }
    }, [onCollapseChange, isCollapsed]);

    const toggleCollapse = () => {
        userToggled.current = true;
        const newState = !isCollapsed;
        setIsCollapsed(newState);
        if (onCollapseChange) {
            onCollapseChange(newState);
        }
    };

    return (
        <aside
            className={`${
                isCollapsed ? "w-16" : "w-64"
            } bg-white dark:bg-primary shadow-md transition-all duration-300 h-screen fixed left-0 top-0 pt-16 z-0 flex flex-col`}
        >
            <div className="p-4">
                <button
                    onClick={toggleCollapse}
                    className="flex items-center justify-center w-8 h-8 rounded-md bg-gray-200 dark:bg-gray-700"
                >
                    {!isCollapsed ? (
                        <IconLayoutSidebarLeftCollapse stroke={2} />
                    ) : (
                        <IconLayoutSidebarRightCollapse stroke={2} />
                    )}
                </button>
            </div>

            <nav className="mt-4 flex-grow">
                <ul className="space-y-2 px-3">
                    <SidebarItem path="/" icon="home" isCollapsed={isCollapsed}>
                        Home
                    </SidebarItem>

                    {tools.map((tool) => (
                        <SidebarItem
                            key={tool.path}
                            path={tool.path}
                            icon={tool.icon}
                            isCollapsed={isCollapsed}
                        >
                            {tool.name}
                        </SidebarItem>
                    ))}
                </ul>
            </nav>

            {/* Logo at the bottom of sidebar */}
            <div
                className={`mt-auto p-4 flex ${
                    isCollapsed ? "justify-center" : "justify-start"
                } items-center border-gray-200 dark:border-gray-700`}
            >
                <img
                    src="assets/logo.png"
                    alt="DevsBestFriend Logo"
                    className={`cursor-pointer transition-all duration-300 ease-in-out transform hover:scale-110 ${
                        isCollapsed ? "w-8 h-8" : "w-36 h-36"
                    } mx-auto animate-pulse hover:animate-none`}
                    onClick={() => setShowDialog(true)}
                    style={{
                        animation: isCollapsed
                            ? "spin 10s linear infinite"
                            : "none",
                        animationPlayState: isCollapsed ? "running" : "paused",
                    }}
                    onMouseEnter={(e) =>
                        (e.currentTarget.style.animationPlayState = "paused")
                    }
                    onMouseLeave={(e) =>
                        (e.currentTarget.style.animationPlayState = "running")
                    }
                />
            </div>

            {/* Motivational Dialog */}
            {showDialog && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
                    onClick={() => setShowDialog(false)}
                >
                    <div
                        className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl max-w-md transform transition-all animate-fade-in"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="text-center">
                            <h3 className="text-2xl font-bold mb-2 text-blue-600 dark:text-blue-400">
                                I Got You, Buddy!
                            </h3>
                            <p className="text-lg mb-4 italic">
                                Let developers help developers.
                            </p>
                            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-2">
                                <button
                                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors duration-200"
                                    onClick={() => setShowDialog(false)}
                                >
                                    Oh Yes! <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="red"
                                        viewBox="0 0 24 24"
                                        strokeWidth={0}
                                        stroke="none"
                                        className="w-5 h-5 inline-block ml-1"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </aside>
    );
};

export default Sidebar;
